import React, { useState, useEffect } from 'react';
import useSync from '../hooks/useSync';
import { recoverFromLegacySync, syncAppData } from '../services/appSyncService';
import { needsLegacyRecovery, markLegacyRecoveryDone } from '../services/migration';
import { getSyncConfig } from '@monorepo/shared';
import './SyncConfigSection.css';

function SyncConfigSection() {
  const { config, updateConfig, syncStatus: status, lastSynced, forceSync } = useSync();
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [saved, setSaved] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [showRecovery, setShowRecovery] = useState(needsLegacyRecovery);
  const [syncing, setSyncing] = useState(false);

  const handleFullSync = async () => {
    setSyncing(true);
    try {
      await forceSync();
      const cfg = await getSyncConfig();
      if (cfg?.apiEndpoint) {
        await syncAppData(cfg.apiEndpoint);
      }
    } catch (err) {
      console.error('Sync failed:', err);
    }
    setSyncing(false);
  };

  useEffect(() => {
    if (config?.apiEndpoint) {
      setApiEndpoint(config.apiEndpoint);
    }
  }, [config]);

  const handleSave = async () => {
    const endpoint = apiEndpoint.replace(/\/+$/, '');
    await updateConfig({ ...config, apiEndpoint: endpoint });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleRecover = async () => {
    const cfg = await getSyncConfig();
    if (!cfg?.apiEndpoint) return;
    setRecovering(true);
    try {
      await recoverFromLegacySync(cfg.apiEndpoint);
      markLegacyRecoveryDone();
      setShowRecovery(false);
    } catch (err) {
      console.error('Recovery failed:', err);
    }
    setRecovering(false);
  };

  return (
    <div className="sync-config-section">
      <h3 className="sync-config-section__title">Cloud Sync (S3)</h3>
      <p className="sync-config-section__description">
        Configure your API endpoint to enable cloud sync. Data syncs automatically
        and works offline — changes queue locally and sync when connectivity returns.
      </p>

      <div className="sync-config-section__field">
        <label htmlFor="sync-api-endpoint">API Endpoint URL</label>
        <input
          id="sync-api-endpoint"
          type="url"
          className="sync-config-section__input"
          value={apiEndpoint}
          onChange={(e) => setApiEndpoint(e.target.value)}
          placeholder="https://xxxxxxxx.execute-api.us-east-1.amazonaws.com"
        />
      </div>

      <div className="sync-config-section__actions">
        <button
          className="sync-config-section__save-btn"
          onClick={handleSave}
        >
          Save
        </button>
        {config?.apiEndpoint && (
          <button
            className="sync-config-section__sync-btn"
            onClick={handleFullSync}
            disabled={status === 'syncing' || syncing || !navigator.onLine}
          >
            {status === 'syncing' || syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        )}
      </div>

      {saved && (
        <p className="sync-config-section__saved" role="status">
          Configuration saved.
        </p>
      )}

      {config?.apiEndpoint && (
        <div className="sync-config-section__status">
          <p>Status: <strong>{status}</strong></p>
          {lastSynced && (
            <p>Last synced: {lastSynced.toLocaleString()}</p>
          )}
        </div>
      )}

      {showRecovery && config?.apiEndpoint && (
        <div className="sync-config-section__recovery">
          <p>Data recovery available — pull your data from S3.</p>
          <button
            className="sync-config-section__sync-btn"
            onClick={handleRecover}
            disabled={recovering}
          >
            {recovering ? 'Recovering...' : 'Recover Data from S3'}
          </button>
        </div>
      )}
    </div>
  );
}

export default SyncConfigSection;
